import SECURITY_API from 'api/security/v1'
import USER_API from 'api/user/v1'
import { push } from 'connected-react-router'
import i18next from 'i18next'
import { startSubmit, stopSubmit, SubmissionError } from 'redux-form'
import { call, put, select, takeLatest } from 'redux-saga/effects'
import extractErrors from 'utils/extractErrors'
import { BROADCAST_MESSAGE_TYPES, postBroadcastMessage } from 'utils/broadcastChannel'
import * as Sentry from '@sentry/browser'
import { ROUTES } from 'constants/routes'
import { generatePath } from 'react-router'
import { notificationShow } from 'modules/Notifications/actions'
import {
  ACTIVATE,
  ADMITAD_AUTH,
  EMAIL_REQUEST_CONFIRMATION,
  LOGIN_SUBMIT,
  LOGOUT,
  RECOVER_PASSWORD,
  RECOVER_SUBMIT,
  REGISTRATION_SUBMIT,
  SET_AUTH,
  TRY_START_SESSION,
  SET_DATA,
  TWO_FACTOR,
} from 'modules/Auth/types'
import { SET_USER_INFO } from 'modules/App/types'
import { ensureAuthorizedShellI18nLoaded } from 'i18nProduct'
import { selectAppLocale } from 'modules/App/store/selectors'

function* registration({ payload: { data, formName }, meta }) {
  const i18n = yield select(selectAppLocale)

  try {
    yield put(startSubmit(formName))

    const { response, error } = yield call(SECURITY_API.registration, { data })

    if (error) {
      throw error
    }

    const bootstrapped = yield call(bootstrapAuthFromTokens, response?.data?.token)

    if (!bootstrapped) {
      yield put(
        notificationShow({
          type: 'error',
          message: 'common::notifications.data_fetch_failed',
        }),
      )
      meta.reject()
      return
    }

    yield put(
      notificationShow({
        type: 'success',
        message: 'auth::forms.registration.notifications.success',
        delay: 6000,
      }),
    )

    yield put(push(generatePath(ROUTES.SERVICES, { i18n })))

    meta.resolve()
  } catch (error) {
    const { form, fields } = extractErrors(error.errors)

    if (fields) {
      meta.reject(new SubmissionError(fields))
      Sentry.captureMessage('Registration fail cause fields: ' + Object.keys(fields).join(', '))
    }

    if (form?.length) {
      Sentry.captureMessage('Registration fail cause form error: ' + form)

      yield put(
        notificationShow({
          type: 'error',
          message: form,
        }),
      )

      meta.reject()
    } else {
      meta.reject(error)
    }

    console.logXHR(error)
  } finally {
    yield put(stopSubmit(formName))
  }
}

function* recover({ payload: { data, formName }, meta }) {
  try {
    yield put(startSubmit(formName))

    const { error } = yield call(SECURITY_API.recover, { data })

    if (error) {
      throw error
    }

    yield put(
      notificationShow({
        type: 'success',
        message: 'auth::forms.recover.notifications.success',
        delay: 6000,
      }),
    )

    meta.resolve()
  } catch (error) {
    const { form, fields } = extractErrors(error.errors)

    fields && meta.reject(new SubmissionError(fields))

    if (form?.length) {
      yield put(
        notificationShow({
          type: 'error',
          message: form,
        }),
      )
    }

    console.logXHR(error)
  } finally {
    yield put(stopSubmit(formName))
  }
}

function* recoverPassword({ payload: { data, formName }, meta }) {
  try {
    yield put(startSubmit(formName))

    const { error } = yield call(SECURITY_API.setPassword, { data })

    if (error) {
      throw error
    }

    yield put(
      notificationShow({
        type: 'success',
        message: 'auth::forms.recover_password.notifications.success',
        delay: 6000,
      }),
    )

    meta.resolve()
  } catch (error) {
    const { form, fields } = extractErrors(error.errors)

    fields && meta.reject(new SubmissionError(fields))

    if (form?.length) {
      yield put(
        notificationShow({
          type: 'error',
          message: form,
        }),
      )
    }

    console.logXHR(error)
  } finally {
    yield put(stopSubmit(formName))
  }
}

function* activate({ payload: { data, formName }, meta }) {
  const i18n = yield select(selectAppLocale)

  try {
    yield put(startSubmit(formName))

    const { response, error } = yield call(SECURITY_API.activate, { data })

    if (error) {
      throw error
    }

    const tokenPayload = response?.data?.token

    if (!persistAuthTokens(tokenPayload)) {
      yield put(
        notificationShow({
          type: 'error',
          message: 'common::notifications.data_fetch_failed',
        }),
      )
      meta.reject()
      return
    }

    postBroadcastMessage(BROADCAST_MESSAGE_TYPES.EMAIL_CONFIRMED)

    const bootstrapped = yield call(completeAuthSession)

    if (!bootstrapped) {
      yield put(
        notificationShow({
          type: 'error',
          message: 'common::notifications.data_fetch_failed',
        }),
      )
      meta.reject()
      return
    }

    yield put(
      notificationShow({
        type: 'success',
        message: 'auth::forms.activation.notifications.success',
        delay: 6000,
      }),
    )

    yield put(push(generatePath(ROUTES.SERVICES, { i18n })))

    meta.resolve()
  } catch (error) {
    const { form, fields } = extractErrors(error.errors)
    const fieldErrors = fields && Object.keys(fields).length > 0 ? fields : null

    if (fieldErrors) {
      Sentry.captureMessage('Activate fail cause fields: ' + Object.keys(fieldErrors).join(', '))

      meta.reject(new SubmissionError(fieldErrors))
    } else if (form?.length) {
      Sentry.captureMessage('Activate fail cause form error: ' + form)

      yield put(
        notificationShow({
          type: 'error',
          message: form,
        }),
      )

      meta.reject()
    } else {
      meta.reject(error)
    }

    console.logXHR(error)
  } finally {
    yield put(stopSubmit(formName))
  }
}

function* login({ payload: { data, formName }, meta }) {
  const i18n = yield select(selectAppLocale)

  try {
    yield put(startSubmit(formName))
    const { response, error } = yield call(SECURITY_API.login, { data })

    if (error) {
      yield put({ type: SET_AUTH, payload: false })
      throw error
    }

    if (response.data?.two_factor?.required) {
      yield put(push(generatePath(ROUTES.AUTH_LOGIN_2FA, { i18n })))
      meta.resolve()
      return
    }

    const bootstrapped = yield call(bootstrapAuthFromTokens, response?.data?.token)

    if (!bootstrapped) {
      yield put({ type: SET_AUTH, payload: false })
      yield put(
        notificationShow({
          type: 'error',
          message: 'common::notifications.data_fetch_failed',
        }),
      )
      meta.reject()
      return
    }

    yield put(push(generatePath(ROUTES.SERVICES, { i18n })))

    meta.resolve()
  } catch (error) {
    const { form, fields } = extractErrors(error.errors)

    const fieldErrors = fields && Object.keys(fields).length > 0 ? fields : null

    if (fieldErrors) {
      Sentry.captureMessage('Login fail cause fields: ' + Object.keys(fields).join(', '))

      meta.reject(new SubmissionError(fieldErrors))
    } else if (form?.length) {
      Sentry.captureMessage('Login fail cause form error: ' + form)

      yield put(
        notificationShow({
          type: 'error',
          message: form,
        }),
      )
      meta.reject()
    } else {
      meta.reject(error)
    }

    console.logXHR(error)
  } finally {
    yield put(stopSubmit(formName))
  }
}

function* twoFactor({ payload: { data }, meta, formName }) {
  const i18n = yield select(selectAppLocale)
  try {
    yield put(startSubmit(formName))

    const { response, error } = yield call(SECURITY_API.twoFactor, {
      data: data,
    })

    if (error) {
      throw error
    }

    if (response.data.two_factor) {
      yield put(push(generatePath(ROUTES.AUTH_LOGIN_2FA, { i18n })))
    }

    if (response.data.token) {
      yield put({ type: TRY_START_SESSION })
      localStorage.setItem('access_token', response.data.token.access_token)
      localStorage.setItem('refresh_token', response.data.token.refresh_token)
      yield put(push(generatePath(ROUTES.SERVICES, { i18n })))
    }
    meta.resolve()
  } catch (error) {
    const { form, fields } = extractErrors(error.errors)

    fields && meta.reject(new SubmissionError(fields))

    if (form?.length) {
      yield put(
        notificationShow({
          type: 'error',
          message: form,
        }),
      )
    }

    meta.reject()
  } finally {
    yield put(stopSubmit(formName))
  }
}

function* logout() {
  const i18n = yield select(selectAppLocale)
  const hadTokens = localStorage.getItem('access_token') || localStorage.getItem('refresh_token')

  localStorage.removeItem('su')
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')

  yield put(push(generatePath(ROUTES.AUTH_LOGIN, { i18n })))

  if (hadTokens) {
    yield call(SECURITY_API.logout)
  }
}

function persistAuthTokens(tokenPayload) {
  if (!tokenPayload?.access_token || !tokenPayload?.refresh_token) {
    return false
  }

  localStorage.setItem('access_token', tokenPayload.access_token)
  localStorage.setItem('refresh_token', tokenPayload.refresh_token)

  return true
}

function* completeAuthSession() {
  const { response: userResponse, error: userError } = yield call(USER_API.getUserInfo)

  if (userError) {
    throw userError
  }

  yield call(startSession, { user: userResponse.data })
  Sentry.setUser({ email: userResponse.data.email, id: userResponse.data.id })

  return true
}

function* bootstrapAuthFromTokens(tokenPayload) {
  if (!persistAuthTokens(tokenPayload)) {
    return false
  }

  return yield call(completeAuthSession)
}

function* startSession({ user }) {
  const { error } = yield call(SECURITY_API.start)

  if (error) {
    throw error
  }

  yield put({
    type: SET_USER_INFO,
    payload: { user },
  })

  const locale = yield select(selectAppLocale)
  yield call(ensureAuthorizedShellI18nLoaded, { locale })

  yield put({
    type: SET_AUTH,
    payload: true,
  })
}

function* start() {
  yield put({ type: SET_DATA, payload: { loading: true } })

  try {
    const { response, error } = yield call(USER_API.getUserInfo)

    if (error) {
      if (error.status === 403 && global.yaCounter32793525) {
        global.yaCounter32793525.reachGoal('user-403')
      }

      if (error.status === 401 || error.status === 403) {
        throw error
      }

      yield put({ type: SET_DATA, payload: { loading: false } })
      yield put(
        notificationShow({
          type: 'error',
          message: 'common::notifications.data_fetch_failed',
        }),
      )
      return
    }

    yield call(startSession, { user: response.data })
    Sentry.setUser({ email: response.data.email, id: response.data.id })
  } catch (e) {
    yield put({ type: LOGOUT })
  }
}

function* admitadAuth({ payload: { code } }) {
  const i18n = yield select(selectAppLocale)
  try {
    const { response, error } = yield call(SECURITY_API.admitadOAuthApply, {
      params: { code },
    })

    if (error) {
      throw error
    }

    if (response.data.two_factor) {
      yield put(push(generatePath(ROUTES.AUTH_LOGIN_2FA, { i18n })))
    }

    if (response.data.token) {
      yield put({ type: TRY_START_SESSION })
      localStorage.setItem('access_token', response.data.token.access_token)
      localStorage.setItem('refresh_token', response.data.token.refresh_token)
      yield put({ type: SET_AUTH, payload: true })
      yield put(push(generatePath(ROUTES.SERVICES, { i18n })))
    }
  } catch (error) {
    yield put(push(generatePath(ROUTES.AUTH_LOGIN, { i18n })))

    const { form } = extractErrors(error.errors)

    if (form?.length) {
      yield put(
        notificationShow({
          type: 'error',
          message: i18next.t(form),
        }),
      )
    }
  }
}

function* emailRequestConfirmation() {
  try {
    const { response, error } = yield call(SECURITY_API.emailRequestConfirmation)
    if (error) {
      throw error
    }

    if (response) {
      yield put(
        notificationShow({
          type: 'success',
          message: 'user::settings.profile.email_sent',
        }),
      )
    }
  } catch (error) {
    const { form } = extractErrors(error.errors)

    if (form?.length) {
      yield put(
        notificationShow({
          type: 'error',
          message: i18next.t(form),
        }),
      )
    }
  }
}

export default function* Sagas() {
  yield takeLatest(REGISTRATION_SUBMIT, registration)
  yield takeLatest(RECOVER_SUBMIT, recover)
  yield takeLatest(RECOVER_PASSWORD, recoverPassword)
  yield takeLatest(LOGIN_SUBMIT, login)
  yield takeLatest(ACTIVATE, activate)
  yield takeLatest(LOGOUT, logout)
  yield takeLatest(TWO_FACTOR, twoFactor)
  yield takeLatest(TRY_START_SESSION, start)
  yield takeLatest(ADMITAD_AUTH, admitadAuth)
  yield takeLatest(EMAIL_REQUEST_CONFIRMATION, emailRequestConfirmation)
}
